import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Select from 'react-select';
import SortableTree from 'react-sortable-tree';
import 'react-sortable-tree/style.css';
import Hosts from '../../Hosts';
import Loader from '../Loader/Loader';
import Swal from 'sweetalert2';
import 'react-phone-number-input/style.css';
import PhoneInput from 'react-phone-number-input';

import {
	reqGET,
	reqPOST,
	reqPUT,
	reqFILE,
	obter_cidades_por_distrito,
	obter_paises_em_pt,
	obter_distritos_pt,
	pattern_vat,
	pattern_email,
	handle_compressed_image_upload,
	set_language
} from '../../Utils';

import { language as language_web } from '../WEBContent/Language';
import { language as language_registform } from './Language';
import { color } from '../WEBContent/Color';

const language = {
	pt: { ...language_web.pt, ...language_registform.pt },
	en: { ...language_web.en, ...language_registform.en },
	es: { ...language_web.es, ...language_registform.es },
};

// ────────────────────────────────────────────────────────────────────────
// RegistForm.js — Formulário público de inscrição (FDI-Portugal).
//
// Segue o mesmo padrão do RegistForm.js do Login (dados = useRef + set_dados,
// Hosts/Utils em src/, language partilhado em WEBContent/Language), mas sem
// Config.js/Language.js locais — a "data_interface" é definida aqui mesmo,
// e não depende de sessão iniciada (getTokenInfo), por ser um formulário
// público sem autenticação.
//
// TODO a confirmar por ti:
//   1) COMPANYCODE — de onde vem o código da federação/empresa para este
//      formulário público? Por agora aceita via props.companycode (com
//      fallback vazio). Ajusta a origem se vier de outro sítio (ex: subdomínio,
//      query string, ou uma constante fixa em Hosts.js).
//   2) API_CRUD_ROUTE — a rota real do endpoint de inscrições. Defini um
//      valor plausível (`${Hosts.SIMCore_API}/entities/inscricao`) — confirma
//      e ajusta conforme a tua API.
// ────────────────────────────────────────────────────────────────────────

const COMPANYCODE_FALLBACK = ''; // TODO: confirmar origem do companycode neste formulário público
const api_crud_base = Hosts.SIMCore_API; // TODO: confirmar
const api_crud_route = `${Hosts.SIMCore_API}/fdi/entities/`; // TODO: confirmar rota real

// Estrutura de dados "em branco" do formulário — à semelhança do
// data_interface que o RegistForm.js do Login já define inline dentro do
// useEffect, mas aqui como função para devolver sempre uma cópia nova.
const get_empty_data = (companycode = COMPANYCODE_FALLBACK) => ({
	id: '',
	info: {
		companycode: `${Hosts.companycode}`,
		username: '',
		password: '',
		name: '',
		foto: [],
		rgpd: false,
		ativo: false,
		birthday: '',
		vat: '',
		identificacao: {
			tipo_identificacao: '',
			numero_identificacao: '',
			validade_identificacao: '',
			iban: '',
			nbss: '',
			ass: '',
			arquivo: '',
			validade_contribuinte: '',
		},		
		
		numero_licenca: '',
		funcao: '',
		modalidade: null,
		escalao: '',
		epoca: EPOCA_ATUAL,
		clube: null,
		seguro_opcional: '0',
		federado_outras_modalidades: '',

		nacionalidade: '',
		naturalidade: '',
		genero: '',
		profissao: '',
		estabelecimento_ensino: '',

		exame_medico: [],
		data_emissao_exame_medico: '',

		nome_contacto_emergencia: '',
		parentesco: '',
		contacto_emergencia: '',

		observation: '',

		type: [],
        relacao: [],

		profile: {name: ''},

		attributes: {
			entity_category: '1',
		}
	},
	addresses: [{
		village: '',
		city: '',
		region: '',
		country: '',
		localidade: '',
		postal_code: '',
		address_detail: '',
		principal: true,
	}],
	contacts: [{
		name: '',
		email: '',
		phone: '',
		mobile_phone: '',
		principal: true,
	}],
            
	profile: {id: "11", name: "Entidade"},
	notifications: []
});

// Época desportiva atual, calculada a partir da data de hoje.
// Corte em Junho: de Junho em diante já conta a época seguinte
// (ex: em Março de 2026 -> "2025/26"; em Junho de 2026 -> "2026/27").
const getEpocaAtual = () => {
	const hoje = new Date();
	const anoAtual = hoje.getFullYear();
	const mesAtual = hoje.getMonth(); // 0 = Janeiro ... 5 = Junho
	const anoInicioEpoca = mesAtual >= 5 ? anoAtual : anoAtual - 1;
	return `${anoInicioEpoca}/${String(anoInicioEpoca + 1).slice(-2)}`;
};

// Gera a lista de épocas disponíveis à volta da atual (por omissão: 1 antes, 1 depois),
// para permitir revalidar inscrições de épocas anteriores ou já da seguinte.
const gerarListaEpocas = (janelaAntes = 1, janelaDepois = 1) => {
	const anoInicioAtual = parseInt(getEpocaAtual().split('/')[0], 10);
	const lista = [];
	for (let i = -janelaAntes; i <= janelaDepois; i++) {
		const anoInicio = anoInicioAtual + i;
		lista.push(`${anoInicio}/${String(anoInicio + 1).slice(-2)}`);
	}
	return lista;
};

const EPOCA_ATUAL = getEpocaAtual();
const EPOCAS = gerarListaEpocas(1, 1);

// Mesma lista de modalidades usada no módulo das Competições
const modalidades_lista = [
	{ value: 'ski_de_fundo', label: 'Esqui de Fundo' },
	{ value: 'curling', label: 'Curling' },
	{ value: 'ski_alpino', label: 'Esqui alpino' },
	{ value: 'bobsled', label: 'Bobsled' },
	{ value: 'hoquei_no_gelo', label: 'Hóquei no gelo' },
	{ value: 'luge', label: 'Luge' },
	{ value: 'snowboard', label: 'Snowboard' },
	{ value: 'skeleton', label: 'Skeleton' },
	{ value: 'patinacao_artistica', label: 'Patinação artística' },
	{ value: 'patinacao_de_velocidade', label: 'Patinação de velocidade' },
	{ value: 'ski_freestyle', label: 'Esqui Free Style' },
	{ value: 'biatlo', label: 'Biatlo' },
];

// ================= TABELAS DE ESCALÃO (idênticas às Competições) =================
const ESCALOES_GERAL = [
	{ id: 'U10', label: 'U10', min: 2016, max: Infinity },
	{ id: 'U12', label: 'U12', min: 2014, max: 2015 },
	{ id: 'U14', label: 'U14', min: 2012, max: 2013 },
	{ id: 'U16', label: 'U16', min: 2010, max: 2011 },
	{ id: 'U18', label: 'U18', min: 2008, max: 2009 },
	{ id: 'U21', label: 'U21', min: 2005, max: 2007 },
	{ id: 'SENIOR', label: 'Sénior', min: 1991, max: 2004 },
	{ id: 'M35', label: 'M35', min: 1971, max: 1990 },
	{ id: 'M55', label: 'M55', min: -Infinity, max: 1970 },
];

const ESCALOES_SNOWBOARD = [
	{ id: 'U9', label: 'U9 (kids)', min: 2017, max: Infinity },
	{ id: 'U11', label: 'U11 (SuperGrom)', min: 2015, max: 2016 },
	{ id: 'U13', label: 'U13 (SuperGrom)', min: 2013, max: 2014 },
	{ id: 'U15', label: 'U15 (Grom)', min: 2011, max: 2012 },
	{ id: 'U18_ROOKIE', label: 'U18 (Rookie)', min: 2008, max: 2010 },
	{ id: 'ABSOLUTO', label: 'Absoluto', min: 1985, max: 2007 },
	{ id: 'M40', label: 'M40 (Master)', min: -Infinity, max: 1984 },
];

const ESCALOES_PATINAGEM = [
	{ id: 'PRE_INICIADO', label: 'Pré-Iniciado' },
	{ id: 'NOVATOS', label: 'Novatos' },
	{ id: 'JUNIORES', label: 'Juniores' },
	{ id: 'SENIORES', label: 'Seniores' },
	{ id: 'ADULTOS', label: 'Adultos' },
];

const OPCOES_ESCALAO = {
	geral: ESCALOES_GERAL,
	snowboard: ESCALOES_SNOWBOARD,
	patinagem: ESCALOES_PATINAGEM,
};

const anoNascimento = (birthday) => {
	if (!birthday) return null;
	const ano = parseInt(String(birthday).slice(0, 4), 10);
	return Number.isNaN(ano) ? null : ano;
};

const escalaoPorAno = (ano, tabela) => {
	if (ano === null) return '';
	const item = tabela.find(e => ano >= e.min && ano <= e.max);
	return item ? item.id : '';
};

// Idade de referência = idade que o atleta tem a 1 de julho do ano em que a época começa
const idadeReferencia = (birthday, anoEpoca) => {
	if (!birthday) return null;
	const nascimento = new Date(birthday);
	const referencia = new Date(anoEpoca, 6, 1); // 1 de julho
	let idade = referencia.getFullYear() - nascimento.getFullYear();
	const diffMes = referencia.getMonth() - nascimento.getMonth();
	if (diffMes < 0 || (diffMes === 0 && referencia.getDate() < nascimento.getDate())) idade--;
	return idade;
};

// Faixas sobrepostas (Novatos/Juniores/Seniores) -> prioridade: mais "novo" primeiro
const escalaoPorIdade = (idade) => {
	if (idade === null) return '';
	if (idade >= 28) return 'ADULTOS';
	if (idade > 13 && idade <= 18) return 'JUNIORES';
	if (idade >= 8 && idade <= 13) return 'NOVATOS';
	if (idade >= 17) return 'SENIORES';
	if (idade >= 6) return 'PRE_INICIADO';
	return '';
};

const detectarGrupoModalidade = (modalidade_value) => {
	const v = (modalidade_value ?? '').toString().toLowerCase();
	if (v.includes('patinacao')) return 'patinagem';
	if (v.includes('snowboard')) return 'snowboard';
	return 'geral';
};

// anoEpoca = ano civil em que a época desportiva começa (ex: 2025 para a época 2025/2026)
const calcularEscalao = (birthday, modalidade_value, anoEpoca) => {
	const grupo = detectarGrupoModalidade(modalidade_value);
	if (grupo === 'patinagem') {
		return escalaoPorIdade(idadeReferencia(birthday, anoEpoca));
	}
	const tabela = grupo === 'snowboard' ? ESCALOES_SNOWBOARD : ESCALOES_GERAL;
	return escalaoPorAno(anoNascimento(birthday), tabela);
};

const getAnoEpocaAtual = () => {
	const hoje = new Date();
	const anoAtual = hoje.getFullYear();
	const mesAtual = hoje.getMonth(); // 0 = janeiro, 6 = julho
	// Época muda em julho (mês índice 6)
	return mesAtual >= 6 ? anoAtual : anoAtual - 1;
};

const FUNCOES = [
	{ value: 'arbitro_juiz', label_key: 'arbitro_juiz' },
	{ value: 'director', label_key: 'director' },
	{ value: 'dirigente', label_key: 'dirigente' },
	{ value: 'praticante', label_key: 'praticante' },
	{ value: 'treinador', label_key: 'treinador' },
	{ value: 'colaborador', label_key: 'colaborador' },
	{ value: 'director_tecnico_nacional', label_key: 'director_tecnico_nacional' },
	{ value: 'outros', label_key: 'outros' },
	{ value: 'seleccionador', label_key: 'seleccionador' },
];

export default function RegistForm(props) {
	const [, set_dados] = useState(null);
	const dados = useRef(get_empty_data(props.companycode));
	const { pathname } = useLocation();

	const [lang, set_lang] = useState('pt');

	const [ja_inscrito, set_ja_inscrito] = useState(true);

	const [isloading, setLoading] = useState(true);
	const [refresh, setRefresh] = useState(0);

	const [countries, set_countries] = useState([]);
	const [districts, set_districts] = useState([]);
	const [cities, set_cities] = useState([]);

	const [clubes, setClubes] = useState([]);

	// foto
	const [photo_base64, set_photo_base64] = useState('');
	const [files_image, set_files_image] = useState([]);
	const [image_base64, set_image_base64] = useState([]);

	// exame médico (pdf) — upload múltiplo com lista reordenável (SortableTree),
	// tal como o CV no Regist.js do Login
	const [files_doc, set_files_doc] = useState([]); // Files ainda por enviar (submissão final)
	const [treeDataExameMedico, setTreeDataExameMedico] = useState([]); // nós exibidos na SortableTree

	const INSCRICAO_VALOR = 30.0;
	const SEGURO_OPCIONAL_VALOR = 13.5;

	const total_a_pagar = () => {
		const seguro = dados.current.info.seguro_opcional === '1' ? SEGURO_OPCIONAL_VALOR : 0;
		return (INSCRICAO_VALOR + seguro).toFixed(2);
	};

	const change_lang = (l) => {
		set_language(l);
		set_lang(l);
	};

	// ───────────────────────── carregamento inicial ─────────────────────────

	const clean_data = () => {
		set_dados(dados.current = get_empty_data(props.companycode));
	};

	const fetch_clubes = async () => {
		try {
			const res = await reqGET(`${Hosts.SIMCore_API}/entities/list/${dados.current.info.companycode}/Clube`);
			setClubes(res.data.map(c => ({ value: c.id, label: c.name })));
		} catch (error) {
			console.error('Erro ao buscar clubes:', error);
		}
	};

	// revalidação por N.º de Cartão de Cidadão (à semelhança do texto amarelo
	// da imagem: "ao inserir o seu número de Cartão de Cidadão, a maioria dos
	// campos será, automaticamente, preenchida")
	const revalidar_por_documento = async (numero_identificacao) => {
		if (!numero_identificacao || numero_identificacao.length < 8) return;

		try {
			const res = await reqGET(`${api_crud_base}/entities/documento/${dados.current.info.companycode}/${numero_identificacao}`);
			if (res && res.data && res.data.length > 0) {
				/*
				const prev = res.data[0];
				set_dados(dados.current.info = {
					...dados.current.info,
					...prev.info,
					identificacao: { ...prev.info?.identificacao, numero_identificacao },
				});
				if (prev.addresses && prev.addresses[0]) {
					set_dados(dados.current.addresses[0] = { ...dados.current.addresses[0], ...prev.addresses[0] });
				}
				if (prev.contacts && prev.contacts[0]) {
					set_dados(dados.current.contacts[0] = { ...dados.current.contacts[0], ...prev.contacts[0] });
				}
				setRefresh(r => r + 1);
				*/
				set_ja_inscrito(true)
				Swal.fire('', 'Atleta já inscrito.', 'info');
			}
			else {
				set_ja_inscrito(false)
			}
		} catch (error) {
				set_ja_inscrito(false)
			// sem registo anterior — segue-se preenchimento normal
		}
	};

	useEffect(() => {
		if (!localStorage.getItem('lang')) {
			change_lang('pt');
		} else {
			set_lang(localStorage.getItem('lang'));
		}
	}, [props]);

	useEffect(() => {
		clean_data();
		obter_paises_em_pt(set_countries);
		obter_distritos_pt(set_districts);
		fetch_clubes();
		setLoading(false);
	}, [pathname]);

	// ───────────────────────── handlers de campos ─────────────────────────

	const handle_change_info = (e) => {
		const { name, value } = e.target;
		set_dados(dados.current.info[name] = value);
	};

	const handle_change_identificacao = (e) => {
		const { name, value } = e.target;
		let formattedValue = value;

		if (name === 'validade_contribuinte' || name === 'validade_identificacao') {
			const digits = value.replace(/\D/g, '');

			if (digits.length <= 2) {
				formattedValue = digits;
			} else {
				formattedValue = digits.slice(0, 2) + '/' + digits.slice(2, 4);
			}
		}

		if (!dados.current.info.identificacao) {
			dados.current.info.identificacao = {};
		}

		set_dados(dados.current.info.identificacao[name] = formattedValue);
	};

	const handle_change_addresses = (e, addresses_index = 0) => {
		const { name, value } = e.target;
		set_dados(dados.current.addresses[addresses_index][name] = value);
	};

	const handle_change_contacts = (e, index = 0) => {
		const { name, value } = e.target;

		if(name === 'email') {
			set_dados(dados.current.info[name] = value);
		}

		set_dados(dados.current.contacts[index][name] = value);
	};

	const change_tipo_documento = (tipo) => {
		if (!dados.current.info.identificacao) {
			dados.current.info.identificacao = {};
		}
		set_dados(dados.current.info.identificacao.tipo_identificacao = tipo);
	};

	const change_funcao = (value) => {
		set_dados(dados.current.info.funcao = value);
	};

	const change_country = (e) => {
		set_cities([]);
		set_dados(dados.current.addresses[0].city = '');
		set_dados(dados.current.addresses[0].region = '');
		set_dados(dados.current.addresses[0].country = e.value);

		if (e.value === 'Portugal' && districts.length === 0) {
			obter_distritos_pt(set_districts);
		}
	};

	const change_district = (e) => {
		set_cities([]);
		obter_cidades_por_distrito(e.value, set_cities);
		set_dados(dados.current.addresses[0].city = '');
		set_dados(dados.current.addresses[0].region = e.value);
	};

	const change_modalidade = (selected) => {
		set_dados(dados.current.info.modalidade = selected || null);
		// ao mudar de modalidade, a sugestão de escalão é recalculada pelo useEffect abaixo
	};

	// ───────────────────────── escalão automático ─────────────────────────
	// Sugere o escalão a partir da Data de Nascimento + Modalidade, mas só
	// enquanto o campo não tiver sido alterado manualmente pelo utilizador
	// (escalaoAutoRef guarda a última sugestão automática aplicada).
	const escalaoAutoRef = useRef('');

	useEffect(() => {
		const anoEpoca = getAnoEpocaAtual();
		const sugestao = calcularEscalao(
			dados.current.info.birthday,
			dados.current.info.modalidade?.value,
			anoEpoca
		);

		if (!dados.current.info.escalao || dados.current.info.escalao === escalaoAutoRef.current) {
			escalaoAutoRef.current = sugestao;
			set_dados(dados.current.info.escalao = sugestao);
		}
	}, [dados.current.info.birthday, dados.current.info.modalidade?.value]);

	const grupoModalidadeAtual = detectarGrupoModalidade(dados.current.info.modalidade?.value);
	const opcoesEscalaoAtual = OPCOES_ESCALAO[grupoModalidadeAtual].map(e => ({ value: e.id, label: e.label }));

	const change_escalao = (selected) => {
		// escolha manual: deixa de ser substituída automaticamente até mudar modalidade/nascimento
		escalaoAutoRef.current = '';
		set_dados(dados.current.info.escalao = selected ? selected.value : '');
	};

	const change_clube = (selected) => {
		const value = Array.isArray(selected)
			? selected.map(s => ({ id: s.value, name: s.label }))
			: [];
		set_dados(dados.current.info.clube = value);
	};

	const change_genero = (valor) => {
		set_dados(dados.current.info.genero = valor);
	};

	const change_seguro_opcional = (valor) => {
		set_dados(dados.current.info.seguro_opcional = valor);
		setRefresh(r => r + 1);
	};

	// ───────────────────────── uploads (foto / exame médico) ─────────────────────────

	const convertBase64 = (file) =>
		new Promise((resolve, reject) => {
			const fileReader = new FileReader();
			fileReader.readAsDataURL(file);
			fileReader.onload = () => resolve(fileReader.result);
			fileReader.onerror = (error) => reject(error);
		});

	const onChangeHandlerFoto = async (e) => {
		const file = e.target.files[0];
		if (!file) return;

		if (!file.type.includes('image/')) {
			Swal.fire(language[lang].upload_image_error + file.name, '', 'info');
			return;
		}
		if (file.size >= 1048576) {
			Swal.fire(language[lang].upload_image_error + file.name, '', 'info');
			return;
		}

		let stored = [];
		await handle_compressed_image_upload(file, (valor) => stored.push(valor));

		const base64 = await convertBase64(file);
		set_photo_base64(base64);
		set_image_base64([{ id: '1', name: file.name.split('.')[0] + '.webp', link: base64 }]);
		set_files_image([{ files: stored, order: 1, lang, langid: '1' }]);
	};

	// ── Exame Médico: upload múltiplo com lista SortableTree ──────────────
	const onChangeHandlerFilesPDF = (e) => {
		const { files } = e.target;
		const filesArray = Array.prototype.slice.call(files);

		let novosNos = [];
		let novosFiles = [];

		filesArray.forEach((v) => {
			if (v.type.includes('/pdf') && v.size < 10485760) {
				novosFiles.push(v);
				novosNos.push({
					id: `${Date.now()}_${v.name}`,
					id_doc: '',
					title: v.name,
					link: URL.createObjectURL(v),
					visible: true,
				});
			} else {
				Swal.fire(language[lang].upload_file_error + v.name, '', 'info');
			}
		});

		if (novosNos.length > 0) {
			setTreeDataExameMedico(prev => [...prev, ...novosNos]);
			set_files_doc(prev => [...prev, ...novosFiles]);
		}
	};

	const handle_change_tree_exame_medico = (treeData) => {
		setTreeDataExameMedico(treeData);
	};

	const edit_element_tree_exame_medico = (e) => {
		const { value, dataset } = e.target;
		const { id } = dataset;

		setTreeDataExameMedico(prev => prev.map(no => (no.id === id ? { ...no, title: value } : no)));
	};

	const remove_element_tree_exame_medico = (id) => {
		Swal.fire({
			title: language[lang].delete_title,
			text: language[lang].delete_text,
			icon: 'question',
			showCancelButton: true,
			confirmButtonColor: '#0095e8',
			cancelButtonColor: '#d9214e',
			confirmButtonText: language[lang].delete_confirm,
			cancelButtonText: language[lang].delete_cancel,
		}).then((result) => {
			if (result.isConfirmed) {
				const no = treeDataExameMedico.find(n => n.id === id);
				setTreeDataExameMedico(prev => prev.filter(n => n.id !== id));
				if (no) {
					set_files_doc(prev => prev.filter(f => f.name !== no.title));
				}
			}
		});
	};

	const open_link = (url) => {
		window.open(url, '_blank');
	};

	const copy_clipboard = (url) => {
		navigator.clipboard.writeText(url);
		Swal.fire({
			icon: '',
			title: '',
			text: 'Copiado',
			showConfirmButton: false,
			timer: 1000,
		});
	};

	const uploadFile = async (file, type) => {
		const formData = new FormData();
		formData.append('files', file);
		formData.append('id', dados.current.id);
		formData.append('file_name', file.name);
		formData.append('type', 'content');
		formData.append('companycode', dados.current.info.companycode);
		formData.append('folder', Hosts.APP_REPO_FOLDER + 'upload/content/');

		return reqFILE(`${api_crud_base}/multimedia/file`, formData)
			.then((res) => {
				const entry = {
					name: file.name,
					type: file.type,
					size: file.size,
					path: res.data.path,
					source_name: res.data.name,
					link: res.data.path + '/' + res.data.name,
				};

				if (type === 'photo') {
					dados.current.info.foto = [entry];
				} else if (type === 'exame_medico') {
					dados.current.info.exame_medico = [...(dados.current.info.exame_medico || []), entry];
				}
			})
			.catch((erro) => console.log('Erro POST File: ' + erro));
	};

	// ───────────────────────── submissão ─────────────────────────

	const handleSubmit = async (e) => {
		e.preventDefault();

		try {
			const response = await reqPOST(api_crud_route, dados.current);

			if (files_image.length > 0) {
				for (const el of files_image) {
					if (el.files.length > 0) await uploadFile(el.files[0], 'photo');
				}
			}
			if (files_doc.length > 0) {
				for (const file of files_doc) {
					await uploadFile(file, 'exame_medico');
				}
			}

			if (props.onSuccess) {
				props.onSuccess(response);
			} 
			else if (props.redirect_to && props.history) {
				// props.history.push(props.redirect_to);
			} 
			else {
				// clean_data();
			}
		} catch (error) {
			console.error('Submission error:', error);
			Swal.fire('', language[lang].insert_error, 'info');
		}
	};

	// ───────────────────────── estilos (iguais aos do Regist.js) ─────────────────────────

	const tableStyle = {
		width: '100%',
		borderCollapse: 'collapse',
		fontSize: '14px',
		color: color.cor1.hexa,
		textAlign: 'left',
		boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
		borderRadius: '8px',
		overflow: 'hidden',
		backgroundColor: color.cor3.hexa,
	};

	const dropzoneBoxStyle = {
		border: `2px dashed rgb(${color.cor2.rgb})`,
		borderRadius: '6px',
		padding: '20px 12px',
		textAlign: 'center',
		backgroundColor: '#fafafa',
		color: color.corCinza.hexa,
		fontSize: '13px',
		cursor: 'pointer',
	};

	return isloading ? (
		<Loader />
	) : (
		<main>
			<div className="dash-cards header">
				<h2 className="dash-title">{language[lang].inscricao || 'Formulário de Inscrição'}</h2>
				<p>{language[lang].inscricao_subtitulo || 'Documentos obrigatórios: Foto (JPEG) e Exame Médico (PDF).'}</p>
			</div>

			<div className="dash-cards body">
				<div className="container">
					<div className="row">
						<div className="mb-5 mb-xl-10">
							<div className="card">
								<div className="card-header border-0 d-flex justify-content-sm-between p-4">
									<div className="card-title m-0">
										<h3 className="fw-bolder m-0 text-nowrap">{language[lang].personal_data}</h3>
									</div>
								</div>

								<div id="div-content">
									<form id="form" onSubmit={handleSubmit}>
										<div className="card-body border-top p-4">

											{/* aviso amarelo, tal como na imagem de referência */}
											<div
												className="p-3 mb-4"
												style={{ backgroundColor: '#fff8dd', border: '1px solid #f0e2a4', borderRadius: '6px', fontSize: '13px' }}
											>
												{language[lang].revalidacao_aviso ||
													'Caso esteja a revalidar a sua inscrição numa nova época, ao inserir o seu número de documento, a maioria dos campos será, automaticamente, preenchida (podendo actualizar a informação que for necessária).'}
											</div>

											<div className="row">
												{/* ── coluna principal ───────────────────────────── */}
												<div className="col-md-9">

													<h6 className="fw-bold mb-3 text-uppercase text-muted" style={{ fontSize: '13px' }}>
														{language[lang].identification_number}
													</h6>

													<div className="row g-3 mb-4">
														<div className="col-md-6">
															<label className="form-label fw-bold fs-6">{language[lang].tipo_documento} *</label>
															<div className="d-flex flex-wrap gap-3">
																{['CC', 'BI', 'Aut. Res.', 'Passaporte'].map((tipo) => (
																	<div className="form-check" key={tipo}>
																		<input
																			type="radio"
																			className="form-check-input"
																			id={`tipo-doc-${tipo}`}
																			name="tipo_documento"
																			checked={dados.current.info.identificacao?.tipo_identificacao === tipo}
																			onChange={() => change_tipo_documento(tipo)}
																		/>
																		<label className="form-check-label" htmlFor={`tipo-doc-${tipo}`}>{tipo}</label>
																	</div>
																))}
															</div>
														</div>

														<div className="col-md-3">
															<label className="form-label fw-bold fs-6">{language[lang].numero_documento} *</label>
															<input
																type="text"
																name="numero_identificacao"
																value={dados.current.info.identificacao?.numero_identificacao || ''}
																className="form-control rounded-0"
																onChange={handle_change_identificacao}
																onBlur={(e) => revalidar_por_documento(e.target.value)}
																required
															/>
														</div>

														<div className="col-md-3">
															<label className="form-label fw-bold fs-6">{language[lang].validade}</label>
															<input
																type="text"
																maxLength="5"
																inputMode="numeric"
																placeholder="MM/AA"
																name="validade_identificacao"
																value={dados.current.info.identificacao?.validade_identificacao || ''}
																className="form-control rounded-0"
																onChange={handle_change_identificacao}
															/>
														</div>
													</div>

													<div className="row g-3 mb-4">
														<div className="col-md-6">
															<label className="form-label fw-bold fs-6">{language[lang].numero_contribuinte}</label>
															<input
																type="text"
																pattern={pattern_vat}
																inputMode="numeric"
																name="vat"
																value={dados.current.info.vat || ''}
																className="form-control rounded-0"
																onChange={handle_change_info}
															/>
														</div>

														<div className="col-md-6 d-none">
															<label className="form-label fw-bold fs-6">{language[lang].numero_licenca}</label>
															<input
																type="text"
																name="numero_licenca"
																value={dados.current.info.numero_licenca || ''}
																className="form-control rounded-0"
																onChange={handle_change_info}
															/>
														</div>
													</div>

													<div className="row g-3 mb-4">
														<div className="col-12">
															<label className="form-label fw-bold fs-6">{language[lang].funcao} *</label>
															<div className="d-flex flex-wrap gap-3">
																{FUNCOES.map((f) => (
																	<div className="form-check" key={f.value}>
																		<input
																			type="radio"
																			className="form-check-input"
																			id={`funcao-${f.value}`}
																			name="funcao"
																			checked={dados.current.info.funcao === f.value}
																			onChange={() => change_funcao(f.value)}
																		/>
																		<label className="form-check-label" htmlFor={`funcao-${f.value}`}>
																			{language[lang][f.label_key] || f.label_key}
																		</label>
																	</div>
																))}
															</div>
														</div>
													</div>

													<hr className="my-4" />

													<h6 className="fw-bold mb-3 text-uppercase text-muted" style={{ fontSize: '13px' }}>
														{language[lang].personal_data}
													</h6>

													<div className="row g-3 mb-4">
														<div className="col-12">
															<label className="form-label fw-bold fs-6">{language[lang].name} *</label>
															<input
																type="text"
																name="name"
																value={dados.current.info.name || ''}
																className="form-control rounded-0"
																onChange={handle_change_info}
																required
															/>
														</div>
													</div>

													<div className="row g-3 mb-4">
														<div className="col-md-4">
															<label className="form-label fw-bold fs-6">{language[lang].modalidades}</label>
															<Select
																className="div-select"
																options={modalidades_lista}
																value={dados.current.info.modalidade}
																placeholder={language[lang].select}
																onChange={change_modalidade}
															/>
														</div>

														<div className="col-md-4">
															<label className="form-label fw-bold fs-6">{language[lang].escalao}</label>
															<Select
																className="div-select"
																options={opcoesEscalaoAtual}
																value={opcoesEscalaoAtual.find(o => o.value === dados.current.info.escalao) || null}
																placeholder={language[lang].select}
																onChange={change_escalao}
															/>
														</div>

														<div className="col-md-4">
															<label className="form-label fw-bold fs-6">{language[lang].epoca} *</label>
															<select
																name="epoca"
																className="form-select rounded-0"
																value={dados.current.info.epoca || ''}
																onChange={handle_change_info}
																required
															>
																<option value="">{language[lang].select}</option>
																{EPOCAS.map((ep) => (
																	<option key={ep} value={ep}>{ep}</option>
																))}
															</select>
														</div>
													</div>

													<div className="row g-3 mb-4">
														<div className="col-md-6">
															<label className="form-label fw-bold fs-6">{language[lang].clube}</label>
															<Select
																isMulti
																className="div-select"
																options={clubes}
																value={
																	Array.isArray(dados.current.info.clube)
																		? dados.current.info.clube.map(c => ({ value: c.id, label: c.name }))
																		: []
																}
																placeholder={language[lang].select}
																onChange={change_clube}
															/>
														</div>

														<div className="col-md-6">
															<label className="form-label fw-bold fs-6">{language[lang].seguro_opcional} (€{SEGURO_OPCIONAL_VALOR.toFixed(2)}) *</label>
															<div className="d-flex gap-3 mt-2">
																<div className="form-check">
																	<input
																		type="radio"
																		className="form-check-input"
																		id="seguro-nao"
																		checked={dados.current.info.seguro_opcional === '0' || !dados.current.info.seguro_opcional}
																		onChange={() => change_seguro_opcional('0')}
																	/>
																	<label className="form-check-label" htmlFor="seguro-nao">{language[lang].nao}</label>
																</div>
																<div className="form-check">
																	<input
																		type="radio"
																		className="form-check-input"
																		id="seguro-sim"
																		checked={dados.current.info.seguro_opcional === '1'}
																		onChange={() => change_seguro_opcional('1')}
																	/>
																	<label className="form-check-label" htmlFor="seguro-sim">{language[lang].sim}</label>
																</div>
															</div>
														</div>
													</div>

													<div className="row g-3 mb-4">
														<div className="col-12">
															<label className="form-label fw-bold fs-6">{language[lang].federado_outras_modalidades}</label>
															<textarea
																name="federado_outras_modalidades"
																value={dados.current.info.federado_outras_modalidades || ''}
																className="form-control rounded-0"
																rows="2"
																onChange={handle_change_info}
															/>
														</div>
													</div>

													<hr className="my-4" />

													<h6 className="fw-bold mb-3 text-uppercase text-muted" style={{ fontSize: '13px' }}>
														{language[lang].naturalidade}
													</h6>

													<div className="row g-3 mb-4">
														<div className="col-md-4">
															<label className="form-label fw-bold fs-6">{language[lang].nacionalidade} *</label>
															<input
																type="text"
																name="nacionalidade"
																value={dados.current.info.nacionalidade || ''}
																className="form-control rounded-0"
																onChange={handle_change_info}
																required
															/>
														</div>

														<div className="col-md-4">
															<label className="form-label fw-bold fs-6">{language[lang].naturalidade} *</label>
															<input
																type="text"
																name="naturalidade"
																value={dados.current.info.naturalidade || ''}
																className="form-control rounded-0"
																onChange={handle_change_info}
																required
															/>
														</div>

														<div className="col-md-4">
															<label className="form-label fw-bold fs-6">{language[lang].genero} *</label>
															<div className="d-flex gap-3 mt-2">
																<div className="form-check">
																	<input
																		type="radio"
																		className="form-check-input"
																		id="genero-f"
																		checked={dados.current.info.genero === '2'}
																		onChange={() => change_genero('2')}
																	/>
																	<label className="form-check-label" htmlFor="genero-f">{language[lang].feminino}</label>
																</div>
																<div className="form-check">
																	<input
																		type="radio"
																		className="form-check-input"
																		id="genero-m"
																		checked={dados.current.info.genero === '1'}
																		onChange={() => change_genero('1')}
																	/>
																	<label className="form-check-label" htmlFor="genero-m">{language[lang].masculino}</label>
																</div>															
															</div>
														</div>
													</div>

													<div className="row g-3 mb-4">
														<div className="col-md-4">
															<label className="form-label fw-bold fs-6">{language[lang].birthday} *</label>
															<input
																type="date"
																name="birthday"
																value={dados.current.info.birthday || ''}
																className="form-control rounded-0"
																onChange={handle_change_info}
																required
															/>
														</div>

														<div className="col-md-4">
															<label className="form-label fw-bold fs-6">{language[lang].profissao} *</label>
															<input
																type="text"
																name="profissao"
																value={dados.current.info.profissao || ''}
																className="form-control rounded-0"
																placeholder={language[lang].profissao_placeholder || 'Se for estudante, preencha "Estudante"'}
																onChange={handle_change_info}
																required
															/>
														</div>

														<div className="col-md-4">
															<label className="form-label fw-bold fs-6">{language[lang].estabelecimento_ensino}</label>
															<input
																type="text"
																name="estabelecimento_ensino"
																value={dados.current.info.estabelecimento_ensino || ''}
																className="form-control rounded-0"
																onChange={handle_change_info}
															/>
														</div>
													</div>

													<hr className="my-4" />

													<h6 className="fw-bold mb-3 text-uppercase text-muted" style={{ fontSize: '13px' }}>
														{language[lang].morada}
													</h6>

													<div className="row g-3 mb-4">
														<div className="col-12">
															<label className="form-label fw-bold fs-6">{language[lang].morada} *</label>
															<textarea
																name="address_detail"
																value={dados.current.addresses[0].address_detail || ''}
																className="form-control rounded-0"
																rows="2"
																onChange={handle_change_addresses}
																required
															/>
														</div>
													</div>

													<div className="row g-3 mb-4">
														<div className="col-md-3">
															<label className="form-label fw-bold fs-6">{language[lang].postal_code} *</label>
															<input
																type="text"
																name="postal_code"
																value={dados.current.addresses[0].postal_code || ''}
																className="form-control rounded-0"
																onChange={handle_change_addresses}
																required
															/>
														</div>

														<div className="col-md-4">
															<label className="form-label fw-bold fs-6">{language[lang].local} *</label>
															<input
																type="text"
																name="localidade"
																value={dados.current.addresses[0].localidade || ''}
																className="form-control rounded-0"
																onChange={handle_change_addresses}
																required
															/>
														</div>

														<div className="col-md-5">
															<label className="form-label fw-bold fs-6">{language[lang].district} *</label>
															<input
																type="text"
																name="region"
																value={dados.current.addresses[0].region || ''}
																className="form-control rounded-0"
																onChange={handle_change_addresses}
															/>
														</div>
													</div>

													<div className="row g-3 mb-4">
														<div className="col-md-6">
															<label className="form-label fw-bold fs-6">{language[lang].country} *</label>
															<Select
																className="div-select"
																options={countries}
																value={dados.current.addresses[0].country === '' ? null : { label: dados.current.addresses[0].country, value: dados.current.addresses[0].country }}
																placeholder={language[lang].select}
																onChange={change_country}
															/>
														</div>

														<div className="col-md-6">
															<label className="form-label fw-bold fs-6">{language[lang].email} *</label>
															<input
																type="email"
																pattern={pattern_email}
																name="email"
																value={dados.current.contacts[0].email || ''}
																className="form-control rounded-0"
																onChange={handle_change_contacts}
																required
															/>
														</div>
													</div>

													<div className="row g-3 mb-4">
														<div className="col-md-6">
															<label className="form-label fw-bold fs-6">{language[lang].tlm} *</label>
															<PhoneInput
																defaultCountry="PT"
																className="form-control rounded-0"
																value={dados.current.contacts[0].mobile_phone || null}
																onChange={(v) => set_dados(dados.current.contacts[0].mobile_phone = v)}
															/>
														</div>

														<div className="col-md-6">
															<label className="form-label fw-bold fs-6">{language[lang].telefone}</label>
															<PhoneInput
																defaultCountry="PT"
																className="form-control rounded-0"
																value={dados.current.contacts[0].phone || null}
																onChange={(v) => set_dados(dados.current.contacts[0].phone = v)}
															/>
														</div>
													</div>

													<hr className="my-4" />

													<h6 className="fw-bold mb-3 text-uppercase text-muted" style={{ fontSize: '13px' }}>
														{language[lang].contacto_emergencia}
													</h6>

													<div className="row g-3 mb-4">
														<div className="col-md-5">
															<label className="form-label fw-bold fs-6">{language[lang].nome_contacto_emergencia}</label>
															<input
																type="text"
																name="nome_contacto_emergencia"
																value={dados.current.info.nome_contacto_emergencia || ''}
																className="form-control rounded-0"
																onChange={handle_change_info}
															/>
														</div>

														<div className="col-md-3">
															<label className="form-label fw-bold fs-6">{language[lang].parentesco}</label>
															<input
																type="text"
																name="parentesco"
																value={dados.current.info.parentesco || ''}
																className="form-control rounded-0"
																onChange={handle_change_info}
															/>
														</div>

														<div className="col-md-4">
															<label className="form-label fw-bold fs-6">{language[lang].contacto}</label>
															<PhoneInput
																defaultCountry="PT"
																className="form-control rounded-0"
																value={dados.current.info.contacto_emergencia || null}
																onChange={(v) => set_dados(dados.current.info.contacto_emergencia = v)}
															/>
														</div>
													</div>

													<hr className="my-4" />

													<div className="row g-3 mb-4">
														<div className="col-12">
															<label className="form-label fw-bold fs-6">{language[lang].observacoes_ice4all || 'Observações e situações especiais (ICE 4 ALL)'}</label>
															<textarea
																name="observation"
																value={dados.current.info.observation || ''}
																className="form-control rounded-0"
																rows="4"
																onChange={handle_change_info}
															/>
														</div>
													</div>

													<div className="form-check mb-4">
														<input
															type="checkbox"
															className="form-check-input"
															id="rgpd"
															checked={dados.current.info.rgpd === true}
															onChange={(e) => set_dados(dados.current.info.rgpd = e.target.checked)}
															required
														/>
														<label className="form-check-label" htmlFor="rgpd">
															{language[lang].aceitacao_dados ||
																'A confirmação deste formulário implica a aceitação do processamento dos seus dados pessoais para fins da actividade desportiva da Federação.'}
														</label>
													</div>
												</div>

												{/* ── coluna lateral: foto / exame médico / valores ───────── */}
												<div className="col-md-3">
													<table style={tableStyle} className="mb-4">
														<tbody>
															<tr>
																<td style={{ padding: '12px 15px' }}>
																	<label className="form-label fw-bold fs-6 d-block">{language[lang].fotografia} *</label>
																	<label htmlFor="photo-upload" style={dropzoneBoxStyle}>
																		{photo_base64 || dados.current.info.foto?.length > 0
																			? language[lang].alterar_foto || 'Alterar fotografia'
																			: language[lang].foto_dropzone || 'Arraste, ou clique para escolher, a sua fotografia (JPG/JPEG, máx. 1000kb)'}
																	</label>
																	<input
																		id="photo-upload"
																		type="file"
																		accept="image/jpeg"
																		onChange={onChangeHandlerFoto}
																		className="d-none"
																	/>
																	{photo_base64 && (
																		<img
																			src={photo_base64}
																			alt="preview"
																			className="img-fluid rounded mt-2"
																			style={{ width: '100%', maxHeight: '140px', objectFit: 'cover' }}
																		/>
																	)}
																</td>
															</tr>
															<tr>
																<td style={{ padding: '12px 15px' }}>
																	<label className="form-label fw-bold fs-6 d-block">{language[lang].exame_medico} *</label>

																	<label htmlFor="exame-medico-upload" style={dropzoneBoxStyle} className="mb-2">
																		{language[lang].pdf_dropzone || 'Arraste, ou clique para escolher, o seu exame médico (PDF, máx. 1000kb)'}
																	</label>
																	<input
																		id="exame-medico-upload"
																		type="file"
																		accept="application/pdf"
																		onChange={onChangeHandlerFilesPDF}
																		className="d-none"
																		multiple
																	/>

																	{treeDataExameMedico.length > 0 && (
																		<SortableTree
																			isVirtualized={false}
																			treeData={treeDataExameMedico}
																			maxDepth={1}
																			canDrag={false}
																			onChange={handle_change_tree_exame_medico}
																			getNodeKey={({ node }) => node.id}
																			generateNodeProps={extendedNode => ({
																				title: (
																					<input
																						type="text"
																						className="form-control form-control-sm rounded-0"
																						defaultValue={extendedNode.node.title}
																						data-id={extendedNode.node.id}
																						onBlur={edit_element_tree_exame_medico}
																					/>
																				),
																				buttons: [
																					<button type="button" onClick={() => open_link(extendedNode.node.link)} className="btn btn-icon btn-bg-light btn-active-color-danger btn-sm" title="link"><i className="bi bi-link"></i></button>,
																					<button type="button" onClick={() => copy_clipboard(extendedNode.node.link)} className="btn btn-icon btn-bg-light btn-active-color-danger btn-sm" title="clipboard"><i className="bi bi-clipboard"></i></button>,
																					<button type="button" onClick={() => remove_element_tree_exame_medico(extendedNode.node.id)} className="btn btn-icon btn-bg-light btn-active-color-danger btn-sm" title="remove"><i className="bi bi-trash-fill"></i></button>,
																				],
																			})}
																		/>
																	)}
																</td>
															</tr>
															<tr>
																<td style={{ padding: '12px 15px' }}>
																	<label className="form-label fw-bold fs-6">{language[lang].data_emissao_exame_medico}</label>
																	<input
																		type="date"
																		name="data_emissao_exame_medico"
																		value={dados.current.info.data_emissao_exame_medico || ''}
																		className="form-control rounded-0"
																		onChange={handle_change_info}
																	/>
																</td>
															</tr>
														</tbody>
													</table>

													<table style={tableStyle}>
														<tbody>
															<tr>
																<td style={{ padding: '12px 15px' }}>{language[lang].inscricao || 'Inscrição'}</td>
																<td style={{ padding: '12px 15px', textAlign: 'right' }}>€ {INSCRICAO_VALOR.toFixed(2)}</td>
															</tr>
															{dados.current.info.seguro_opcional === '1' && (
																<tr>
																	<td style={{ padding: '12px 15px' }}>{language[lang].seguro_opcional}</td>
																	<td style={{ padding: '12px 15px', textAlign: 'right' }}>€ {SEGURO_OPCIONAL_VALOR.toFixed(2)}</td>
																</tr>
															)}
															<tr style={{ fontWeight: 700 }}>
																<td style={{ padding: '12px 15px' }}>{language[lang].total_a_pagar || 'Total a pagar'}</td>
																<td style={{ padding: '12px 15px', textAlign: 'right', color: color.corPrimaria?.hexa }}>€ {total_a_pagar()}</td>
															</tr>
														</tbody>
													</table>
												</div>
											</div>
										</div>
									</form>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className="dash-cards footer">
				<nav className="navbar bottom-navbar navbar-light">
					<div className="navbar-footer">
						<ul className="navbar-nav float-left"></ul>
						<ul className="navbar-nav float-right">
							<li className="nav-item">
								<button type="submit" form="form" className={"nav-link"} disabled={(ja_inscrito)} style={{ backgroundColor: color.corPrimaria?.rgb ? `rgb(${color.corPrimaria.rgb})` : undefined, color: '#fff' }}>
									<i className="bi bi-check2-circle me-2"></i>
									<span>{language[lang].confirmar || 'Confirmar'}</span>
								</button>
							</li>
						</ul>
					</div>
				</nav>
			</div>
		</main>
	);
}